/* eslint-disable no-unused-vars */
// react-router-dom components
import { useRef, useState } from "react";
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import scrollToTop from "../utils/scrollToTop";
import Email from "reusables/Contact/Email";

function Footer({ content, mt }) {
  const { company, socials, menus, copyright } = content;
  const logoRef = useRef(null);
  const [logoWidth, setLogoWidth] = useState(0);

  return (
    <MKBox component="footer" mt={mt}>
      <Container>
        <Grid container spacing={menus.length === 0 ? 0 : 3}>
          {/* Logo and Socials */}
          <Grid
            item
            xs={12}
            lg={menus.length === 0 ? 12 : 3}
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: menus.length === 0 ? "center" : "flex-start",
            }}
          >
            <MKBox
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: menus.length === 0 ? "center" : "flex-start",
                gap: 2,
                mt: 1,
              }}
            >
              {/* Row 1: logo + name inline */}
              <MKBox display="flex" alignItems="center" gap={1.5}>
                {company?.image && (
                  <MKBox
                    ref={logoRef}
                    component="img"
                    src={company.image}
                    alt={company.name}
                    onLoad={() => setLogoWidth(logoRef.current?.offsetWidth ?? 0)}
                    sx={{
                      height: "22px",
                      width: "auto",
                      objectFit: "contain",
                      filter: "grayscale(100%)",
                    }}
                  />
                )}
                <MKTypography variant="h5">{company?.name || ""}</MKTypography>
              </MKBox>

              {/* Row 2: socials indented to align with name start */}
              <MKBox
                display="flex"
                flexDirection="row"
                alignItems="center"
                sx={menus.length > 0 && logoWidth ? { pl: `${logoWidth + 10}px` } : {}}
              >
                {socials.map(({ icon, link, email, subject, body }, key) =>
                  email ? (
                    <MKTypography
                      key={key}
                      variant="h5"
                      color="dark"
                      opacity={0.8}
                      mr={key === socials.length - 1 ? 0 : 2.5}
                    >
                      <Email email={email} subject={subject} body={body}>
                        {icon}
                      </Email>
                    </MKTypography>
                  ) : (
                    <MKTypography
                      key={key}
                      component="a"
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      variant="h5"
                      color="dark"
                      opacity={0.8}
                      mr={key === socials.length - 1 ? 0 : 2.5}
                    >
                      {icon}
                    </MKTypography>
                  )
                )}
              </MKBox>
            </MKBox>
          </Grid>

          {/* Menus */}
          {Array.isArray(menus) &&
            menus.length > 0 &&
            menus.map(({ name: title, route, items }, menuKey) => (
              <Grid key={menuKey} item xs={12} sm={6} lg={2} sx={{ mb: 3 }}>
                <MKTypography
                  component={route ? Link : "div"}
                  to={route || ""}
                  display="block"
                  variant="h6"
                  fontWeight="bold"
                  textTransform="capitalize"
                  mb={1}
                  onClick={() => (route ? scrollToTop() : null)}
                >
                  {title}
                </MKTypography>

                {/* Submenu Items */}
                {items && Array.isArray(items) && (
                  <MKBox component="ul" p={0} m={0} sx={{ listStyle: "none" }}>
                    {items.map(({ name, route: subRoute, items: subItems }, itemKey) => (
                      <MKBox key={itemKey} component="li" p={0} m={0} lineHeight={1.25}>
                        {/* {subItems && <Divider sx={{ width: "100%", my: 0.2, borderWidth: 1 }} />} */}
                        {!subItems && (
                          <MKTypography
                            component={subRoute ? Link : "span"}
                            to={subRoute || ""}
                            onClick={() => scrollToTop()}
                            variant="button"
                            fontWeight="regular"
                            textTransform="capitalize"
                          >
                            {name}
                          </MKTypography>
                        )}
                        {/* Nested Submenu Items */}
                        {subItems &&
                          Array.isArray(subItems) &&
                          subItems.map((nestedItem, nestedKey) => (
                            <MKTypography
                              key={nestedKey}
                              component={nestedItem.route ? Link : "span"}
                              to={nestedItem.route || ""}
                              onClick={() => scrollToTop()}
                              variant="button"
                              fontWeight="regular"
                              sx={{ display: "block" }}
                            >
                              {nestedItem.name}
                            </MKTypography>
                          ))}
                        {/* {subItems && <Divider sx={{ width: "100%", my: 0.2, borderWidth: 1 }} />} */}
                        {subItems && <MKBox mb={2} />}
                      </MKBox>
                    ))}
                  </MKBox>
                )}
              </Grid>
            ))}
          <Grid item xs={12} sx={{ textAlign: "center", my: 3 }}>
            {copyright}
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

Footer.propTypes = {
  content: PropTypes.shape({
    company: PropTypes.shape({
      name: PropTypes.string,
      image: PropTypes.string,
    }),
    socials: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.node,
        link: PropTypes.string,
        email: PropTypes.string,
        subject: PropTypes.string,
        body: PropTypes.string,
      })
    ),
    menus: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        items: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            route: PropTypes.string,
          })
        ),
      })
    ),
    copyright: PropTypes.node,
  }),
  mt: PropTypes.number,
};

Footer.defaultProps = {
  content: {
    company: {},
    socials: [],
    menus: [],
    copyright: "",
  },
  mt: 0,
};

export default Footer;
